import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MapInterface } from '../utils/map';
import type { map as mapFactory } from '../utils/map';

type MapFactory = typeof mapFactory;
type MapInstance = ReturnType<typeof mapFactory>;

type LoadersFetchOptions = {
  loaders?: any[]; // or: Loader[] from @loaders.gl/loader-utils
  loadOptions?: any;
  signal?: AbortSignal;
};

export interface UseMapControllerOptions {
  /** Called when a new area is done */
  onAreaChange?: (area: number) => void;
  /** Notify parent when map instance is ready */
  onReady?: (instance: MapInstance) => void;
  fetchTile?: (url: string, options?: LoadersFetchOptions) => Promise<any>;
  /** Notify parent when map instance is disposed */
  onDispose?: () => void;
  /** Enable Backspace/Delete keyboard events for deleting selected features */
  enableDeleteKeys?: boolean;
  accessToken: string;
}

export function useMapController(
  {
    onAreaChange,
    onReady,
    onDispose,
    enableDeleteKeys = true,
    accessToken = '',
    fetchTile
  }: UseMapControllerOptions = { accessToken: '' }
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [instance, setInstance] = useState<MapInstance | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;
    let mapInstance: MapInstance | null = null;

    (async () => {
      try {
        // @ts-ignore
        const { map } = (await import('../utils/map')) as { map: MapFactory };
        if (cancelled || !containerRef.current || mapInstance) return;

        mapInstance = map({
          mapContainer: containerRef.current,
          onAreaChange,
          fetchTile
        });

        await mapInstance.init(accessToken as string);
        if (cancelled) {
          // If unmounted during init, dispose immediately
          mapInstance.dispose();
          mapInstance = null;
          setInstance(null);
          onDispose?.();
          return;
        }
        setInstance(mapInstance);
        onReady?.(mapInstance);
      } catch (error) {
        console.error(
          '[useMapController] Error initializing map: init failed',
          error
        );
      }
    })();

    return () => {
      cancelled = true;
      try {
        mapInstance?.dispose();
      } catch (error) {
        console.error(
          '[useMapController] Error disposing map: dispose failed',
          error
        );
      } finally {
        mapInstance = null;
        setInstance(null);
        onDispose?.();
      }
    };
    // Intentionally to avoid re-init loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteSelected = useCallback(() => {
    if (!instance) return;
    const id = instance.getCurrentSelectedFeatureId();
    if (id != null) instance.deleteFeature(id);
  }, [instance]);

  const onKeyDown = useMemo<React.KeyboardEventHandler<HTMLDivElement>>(
    () =>
      enableDeleteKeys
        ? e => {
            if (e.key === 'Backspace' || e.key === 'Delete') {
              deleteSelected();
            }
          }
        : () => {},
    [enableDeleteKeys, deleteSelected]
  );

  return {
    containerRef,
    instance,
    onKeyDown,
    deleteSelected
  };
}
